import { useCallback, useState, useEffect } from "react";
import { useSelector } from "redux/hooks";
import styles from "../../ChatSidebar.module.scss";
import classNames from "classnames";

import { DeleteChatConfirm } from "../DeleteChatConfirm";
import { Spinner } from "components";
import { getAllChats, getFolder, IChat } from "redux/actions";
import React from "react";
import { ChatItem } from "../ChatItem";
import { ChatSearchItem } from "../ChatItem/ChatSearchItem";
import { useWindowSize } from "hooks/useWindowSize";

import { useToggleSidebar } from "hooks/services/ToggleSidebarProvider";
import { SelectChat } from "../SelectChat/SelectChat";
import { useChatActions } from "hooks/services/ChatServices/useChatAction";
import { useDynamicFolderHeights } from "hooks/services/DynamicFolderCalculation";
import { FormattedMessage } from "react-intl";

// Define types for our date grouping
interface ChatGroup {
    id: string;
    title: string;
    chats: IChat[];
  }
  
  interface MonthGroups {
    [key: string]: { chats: IChat[] };
  }
  
  interface DateGroups {
    today: { chats: IChat[] };
    yesterday: { chats: IChat[] };
    last7Days: { chats: IChat[] };
    last30Days: { chats: IChat[] };
    months: MonthGroups;
  }


// Helper function to check if date is today
const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };
  
  // Helper function to check if date is yesterday
  const isYesterday = (date: Date): boolean => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    );
  };
  
  // Helper function to check if date is within specified days
  const isWithinDays = (date: Date, days: number): boolean => {
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - days);
    return date >= pastDate;
  };
  
  const SIDEBAR_OFFSET = 48;
  const DEFAULT_FOLDER_ID = 0
  // Group chats by date
  const groupChatsByDate = (chats: IChat[]): ChatGroup[] => {
    if (!chats || !chats.length) return [];
  
    // Create empty groups
    const groups: DateGroups = {
      today: { chats: [] },
      yesterday: { chats: [] },
      last7Days: { chats: [] },
      last30Days: { chats: [] },
      months: {}
    };
    
    // Categorize each chat
    chats.forEach(chat => {
      if (!chat.created_at) return;
      
      const createdAtDate = new Date(chat.created_at);
      
      if (isToday(createdAtDate)) {
        groups.today.chats.push(chat);
      }
      else if (isYesterday(createdAtDate)) {
        groups.yesterday.chats.push(chat);
      }
      else if (isWithinDays(createdAtDate, 7)) {
        groups.last7Days.chats.push(chat);
      }
      else if (isWithinDays(createdAtDate, 30)) {
        groups.last30Days.chats.push(chat);
      }
      else {
        // Group by month and year
        const monthYear = createdAtDate.toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!groups.months[monthYear]) {
          groups.months[monthYear] = { chats: [] };
        }
        groups.months[monthYear].chats.push(chat);
      }
    });
    
    // Build the final result with only non-empty groups
    const result: ChatGroup[] = [];
    
    if (groups.today.chats.length > 0) {
      result.push({ id: 'today', title: 'Today', chats: groups.today.chats });
    }
    
    if (groups.yesterday.chats.length > 0) {
      result.push({ id: 'yesterday', title: 'Yesterday', chats: groups.yesterday.chats });
    }
    
    if (groups.last7Days.chats.length > 0) {
      result.push({ id: 'last7Days', title: 'Last 7 Days', chats: groups.last7Days.chats });
    }
    
    if (groups.last30Days.chats.length > 0) {
      result.push({ id: 'last30Days', title: 'Last 30 Days', chats: groups.last30Days.chats });
    }
    
    // Add month groups - sort by date (newest first)
    Object.entries(groups.months)
      .sort(([monthA, _], [monthB, __]) => {
        // Convert month names to dates for comparison
        const dateA = new Date(monthA);
        const dateB = new Date(monthB);
        return dateB.getTime() - dateA.getTime(); // Use getTime() for numeric comparison
      })
      .forEach(([monthYear, monthGroup]) => {
        if (monthGroup.chats.length > 0) {
          result.push({ 
            id: `month-${monthYear}`, 
            title: monthYear, 
            chats: monthGroup.chats 
          });
        }
      });
    
    return result;
  };

interface IProp {
    onSelectChatItem?: (chatItem: IChat | undefined, folder_id: number) => void;
    searchQuery: string;
    setSelectedItem: (chatItem: IChat | undefined) => void;
    selectedItem?: IChat | undefined;
    // setOpenHistory?: React.Dispatch<React.SetStateAction<boolean>>;
    onStartNewChat?: ({ toChat }: { toChat: boolean }) => void;
    perPage: number;
    workspaceListHeight?: number;
}

export const ChatFolders = ({
    onSelectChatItem,
    searchQuery,
    setSelectedItem,
    selectedItem,
    // setOpenHistory,
    onStartNewChat,
    perPage,
    workspaceListHeight
}: IProp) => {
    const { width } = useWindowSize();
    const { toggleSidebar } = useToggleSidebar();
    const { Folders, 
     } = useSelector((state) => state.chatFolderReducer);
    const { PrivateChat } = useSelector((state) => state.chatReducer);
    const [chatDelConfirm, setChatDelConfirm] = useState<Record<number | string, boolean>>({});
    const [chatLoading, setChatLoading] = useState<Record<number, boolean>>({});
    const [isLoadMore, setIsLoadMore] = useState<Record<number, boolean>>({});

    const { loading, handleSubmit, handleSelectId, handleCancel, selectAllChat, setSelectAllChats, selectedChatIds, setSelectedChatIds, setManuallySelectedChatIds } = useChatActions(setChatDelConfirm, onStartNewChat, selectedItem);
    const { folderHeights, folderContainerRef, folderRefs } = useDynamicFolderHeights(Folders);

    const { theme } = useSelector((state) => state.authReducer);

    useEffect(() => {
        Object.entries(selectAllChat).forEach(([folderId, isSelected]) => {
            if (isSelected) {
                const folder = Folders.find((f) => f.id === Number(folderId));
                if (folder) {
                    const allChatIds = folder.chats.map((chat) => chat.id);
                    setSelectedChatIds((prevSelectedChatIds) => ({
                        ...prevSelectedChatIds,
                        [folderId]: allChatIds,
                    }));
                }
            }
        });
    }, [Folders, selectAllChat]);

    const getChats = useCallback(
        (folder: getFolder, nextPage?: number) => {
          
            getAllChats({ search: "", page: nextPage || folder.pagination.page, perPage: perPage > 9 ? perPage : 10, folder_id: folder.id })
                .finally(() => {
                    setChatLoading((prev) => ({ ...prev, [folder.id]: false }));
                    setIsLoadMore((prev) => ({ ...prev, [folder.id]: false }));
                });
        },
        [perPage]
    );
    const onSelectChat = (chatItem: IChat | undefined, folder_id: number) => {
        // setOpenHistory?.(true);
        setSelectedItem(chatItem);
        onSelectChatItem?.(chatItem, folder_id ?? 0);
        if (width && width <= 768) {
            setTimeout(() => toggleSidebar(), 500);
        }
    };

    const handleSelectAllChats = useCallback(
        (isChecked: boolean, folderId: number | string) => {
            const matchedFolder = Folders.find((folder) => folder.id === folderId);
            const allChatIds = matchedFolder?.chats.map((chat) => chat.id) || [];
            setSelectedChatIds((prev) => ({
                ...prev,
                [folderId]: isChecked ? allChatIds : [],
            }));

            setSelectAllChats((prev) => ({ ...prev, [folderId]: isChecked ? true : false }));
        },
        [Folders, setSelectAllChats, setSelectedChatIds]
    );

    const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>, folder: getFolder) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        const atBottom = scrollHeight - (scrollTop + clientHeight) <= 1;

        if (atBottom && !isLoadMore[folder.id] && folder.pagination.page < folder.pagination.lastPage) {
            setIsLoadMore((prev) => ({ ...prev, [folder.id]: true }));
            getChats(folder, folder.pagination.page + 1);
        }
    };

    return (
        <div ref={folderContainerRef}>
            {Folders.map((folder: getFolder) => {

                const showDelete = (selectedChatIds && selectedChatIds[folder.id]?.length >= 1);
                const chatGroups = (
                   folder.chats.length > 0) ? groupChatsByDate(folder.chats) : [];
                return (
                    <React.Fragment key={folder.id}>
                        {(showDelete 
                      ) && (
                            <SelectChat
                                setChatDelConfirm={setChatDelConfirm}
                                loading={ false }
                                handleSelectAllChats={handleSelectAllChats}
                                selectAllChat={selectAllChat}
                                handleCancel={handleCancel}
                                folder_id={folder.id}
                                selectedChatIds={selectedChatIds}
                            />
                        )}
                        <div className={classNames(styles.content, {
                            [styles.isbackdrop]: PrivateChat,
                            [styles.contentScroll]: true,
                        })}
                            ref={folderRefs.current[folder.id]}
                            onScroll={(e) => handleScroll(e, folder)}
                            style={{
                                overflowY: 'auto',
                                maxHeight:
                                 (folderHeights[folder.id] - (workspaceListHeight?? 0)) || `calc(100% - ${SIDEBAR_OFFSET + (workspaceListHeight?? 0)}px)` ,
                                transition: "height 0.3s ease",
                            }}           
                        >
                            {chatLoading[folder.id] ?
                                <div className="flex justify-center items-center py-[4px]">
                                    <Spinner extraSmall />
                                </div> :
                                (folder.id === DEFAULT_FOLDER_ID && folder.chats.length > 0)
                                  ? (
                                  // Display each group with its header
                                  chatGroups.map(group => (
                                    <React.Fragment key={`group-${group.id}`}>
                                        {/* Group Header */}
                                        <div className={classNames("font-inter", styles.dateGroupHeader, {
                                                    [styles.light]: theme === "light",
                                                    [styles.dark]: theme === "dark",
                                                  })}
                                                  >
                                            <span>{group.title}</span>
                                        </div>
                                        
                                        {/* Group Chats */}
                                        {group.chats.map((chatItem, key) => (
                                        <React.Fragment    key={`topic-${key}`}>
                                            <ChatItem
                                                chatItem={chatItem}
                                                selectedItem={selectedItem}
                                                onSelectChat={onSelectChat}
                                                searchQuery={searchQuery}
                                                handleSelectId={handleSelectId}
                                                selectedChatIds={selectedChatIds}
                                                setSelectedChatIds={setSelectedChatIds}
                                                onStartNewChat={onStartNewChat}
                                                setSelectedItem={setSelectedItem}
                                                folder_id={folder.id}
                                                setManuallySelectedChatIds={setManuallySelectedChatIds}
                                            />
                                            {(searchQuery && searchQuery.length > 0) && (
                                                <ChatSearchItem
                                                    chatItem={chatItem}
                                                    onSelectChat={onSelectChat}
                                                    searchQuery={searchQuery}
                                                />
                                            )}
                                        </React.Fragment>
                                    ))}
                                     </React.Fragment>
                                    ))) : (folder.id === DEFAULT_FOLDER_ID && folder.chats.length === 0) ? (
                                            <div className={styles.emptySearchMessage} >
                                                <FormattedMessage id="sidebar.chat.noChat.message" />
                                            </div>
                                      ) : null
                            }
                            {(isLoadMore[folder.id]
                            ) && (
                                <div className="flex items-center justify-center py-2">
                                    <Spinner extraSmall />
                                </div>
                            )}
                        </div>

                        {chatDelConfirm[folder.id] && (
                            <DeleteChatConfirm
                                onCancel={() => {
                                    handleCancel(true, folder.id)
                                    setChatDelConfirm((prev) => ({ ...prev, [folder.id]: false }))
                                }}
                                onSubmit={() => handleSubmit({ searchQuery, folder_id: folder.id })}
                                loading={loading}
                            />
                        )}
                    </React.Fragment>
                );

            })}
        </div>
    );
}